import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { Calendar, Eye, User, ArrowLeft, ArrowRight, Share2, Tag } from 'lucide-react';

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/detail/${slug}`).then(r => setArticle(r.data)).catch(() => setArticle(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="text-[#C5A059] animate-pulse">Yükleniyor...</div></div>;
  if (!article) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="text-[#A1A1AA]">{language === 'tr' ? 'Makale bulunamadı' : 'Article not found'}</div></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="article-detail-page">
      <section className="py-20 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-8" data-testid="article-breadcrumb">
            <Link to="/" className="text-[#52525B] hover:text-[#C5A059] transition-colors">{language === 'tr' ? 'Ana Sayfa' : 'Home'}</Link>
            <span className="text-[#52525B]">/</span>
            <Link to="/articles" className="text-[#52525B] hover:text-[#C5A059] transition-colors">{language === 'tr' ? 'Makaleler' : 'Articles'}</Link>
            <span className="text-[#52525B]">/</span>
            <span className="text-[#A1A1AA]">{article.title}</span>
          </nav>
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-[#C5A059]/10 text-[#C5A059] border-[#C5A059]/20">{article.category}</Badge>
            {article.is_featured && <Badge className="bg-[#C5A059] text-[#0A0A0A]">{language === 'tr' ? 'Öne Çıkan' : 'Featured'}</Badge>}
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F3F4F6] leading-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="article-title">{article.title}</motion.h1>
          <div className="flex items-center gap-6 text-[#A1A1AA] text-sm">
            <span className="flex items-center gap-2"><User className="h-4 w-4 text-[#C5A059]" /> {article.author}</span>
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#C5A059]" /> {new Date(article.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="flex items-center gap-2"><Eye className="h-4 w-4 text-[#C5A059]" /> {article.view_count || 0} {language === 'tr' ? 'görüntülenme' : 'views'}</span>
          </div>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} data-testid="article-content">
            {article.content.split('\n\n').map((paragraph, i) => (
              <p key={i} className="text-[#A1A1AA] leading-relaxed mb-6 text-base">{paragraph}</p>
            ))}
          </motion.div>
          {article.tags && article.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10" data-testid="article-tags">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="h-4 w-4 text-[#C5A059]" />
                {article.tags.map(tag => (
                  <Badge key={tag} className="bg-[#1A1A1A] text-[#A1A1AA] border-white/10 hover:border-[#C5A059]/30 hover:text-[#C5A059] cursor-pointer transition-colors">#{tag}</Badge>
                ))}
              </div>
            </div>
          )}
          <div className="mt-8 pt-8 border-t border-white/10 flex items-center justify-between">
            <Link to="/articles"><Button variant="outline" className="border-white/10 text-[#A1A1AA] hover:text-[#C5A059] hover:border-[#C5A059]/30 rounded-sm" data-testid="back-to-articles"><ArrowLeft className="h-4 w-4 mr-2" /> {language === 'tr' ? 'Tüm Makaleler' : 'All Articles'}</Button></Link>
            <Button variant="ghost" className="text-[#A1A1AA] hover:text-[#C5A059]" onClick={() => navigator.clipboard.writeText(window.location.href)} data-testid="share-article"><Share2 className="h-4 w-4 mr-2" /> {language === 'tr' ? 'Paylaş' : 'Share'}</Button>
          </div>
          {article.related && article.related.length > 0 && (
            <div className="mt-16" data-testid="related-articles">
              <h3 className="text-2xl font-bold text-[#F3F4F6] mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{language === 'tr' ? 'Benzer Makaleler' : 'Related Articles'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {article.related.map((rel, i) => (
                  <Link key={rel.id || i} to={`/articles/${rel.slug}`} className="p-5 bg-[#121212] border border-white/10 rounded-sm card-hover group" data-testid={`related-article-${i}`}>
                    <Badge className="bg-[#C5A059]/10 text-[#C5A059] border-[#C5A059]/20 text-xs mb-3">{rel.category}</Badge>
                    <h4 className="text-[#F3F4F6] font-semibold group-hover:text-[#C5A059] transition-colors line-clamp-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{rel.title}</h4>
                    <p className="text-[#52525B] text-xs mt-2">{new Date(rel.created_at).toLocaleDateString('tr-TR')}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
